import { createRouter } from '@src/composables/router'
import { isAuthenticated } from '@src/utilities/auth'
import { usePreferredSorting } from '@src/router/sorting'
import { useLastAppliedFeedFilter } from '@src/router/lastAppliedFeedFilter'
import { IUnauthenticatedRealmSettings } from '@src/types/realmSettings.types'
import { usersApi } from '@src/api/apiModuleInstances'
import unleashApi from '@src/api/unleash'
import { FeatureToggleNames } from '@src/composables/featureToggles/featureToggle.names'
import { PrivilegedUserRoles } from '@src/types/user.types'
import roleBasedNavigationGuard from './roleBasedNavigationGuard'
import getAdminRouteConfig from './admin'
import getKnowledgeInsightsRouteConfig from './knowledgeInsights'

let routerCreated = false

export const createRouterForVueNativeMode = (realmSettings?: IUnauthenticatedRealmSettings) => {
  if (routerCreated) {
    throw new Error('The router was already created. Use $router in components to get a reference.')
  }

  const router = createRouter({
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      }
      return { left: 0, top: 0 }
    },
    routes: [
      {
        path: '/agent/:conversationId?',
        name: 'agent',
        props: (route) => ({
          conversationId: route.params.conversationId
        }),
        async beforeEnter(to, from, next) {
          const { data: user } = await usersApi.cached.getCurrentUser()
          const { data: { toggles } } = await unleashApi.cached.get(user.event_tracking_id, realmSettings!.metadata.realm_id, realmSettings!.general.default_language)
          // The Agent loads only when it's enabled for the account. Otherwise fall back to
          // StarGPT when that's enabled, or deny access.
          if (toggles.find((t) => t.name === FeatureToggleNames.RELEASE_STARDEV_11519_AGENT_ROLLOUT)?.enabled) {
            next()
          } else if (toggles.find((t) => t.name === FeatureToggleNames.RELEASE_LLM_CONVERSATIONS)?.enabled) {
            next('/conversation')
          } else {
            next('/error/403')
          }
        },
        component: () => import(/* webpackChunkName: "agent" */ '@src/views/KnowledgeEngine/index.vue')
      },
      {
        path: '/connections',
        meta: {
          requiredRole: PrivilegedUserRoles.SuperAdmin
        },
        component: () => import(/* webpackChunkName: "knowledge-insights" */ '@src/views/Connections/index.vue')
      },
      {
        path: '/knowledge-insights',
        meta: {
          requiredRole: PrivilegedUserRoles.NetworkStatisticsAdmin
        },
        component: () => import(/* webpackChunkName: "knowledge-insights" */ '@src/views/KnowledgeInsights/KnowledgeInsightsContainer.vue'),
        children: getKnowledgeInsightsRouteConfig()
      },
      {
        path: '',
        component: () => import(
          /* webpackChunkName: "MainLayout" */ '@src/layouts/MainLayout.vue'
        ),
        children: [
          {
            path: '/question/:id',
            name: 'question',
            component: () => import(/* webpackChunkName: "question" */ '@src/views/Question/index.vue'),
            beforeEnter: usePreferredSorting
          },
          {
            path: '/stream',
            name: 'stream',
            alias: '/feed',
            component: () => import(/* webpackChunkName: "search" */ '@src/views/Stream/index.vue'),
            beforeEnter: [ usePreferredSorting, useLastAppliedFeedFilter ]
          },
          // homepage
          {
            path: '',
            name: 'homepage',
            beforeEnter(to, from, next) {
              if (realmSettings?.homepage.enabled) {
                next()
              } else if (isAuthenticated()) {
                next('/stream')
              }
            },
            component: () => import(/* webpackChunkName: "homepage" */ '@src/views/Homepage/index.vue')
          },
          {
            path: 'dashboard',
            name: 'dashboard',
            redirect: { name: 'homepage' }
          },
          {
            path: 'search/',
            props: (route) => ({ query: route.query.query || '' }),
            component: () => import(/* webpackChunkName: "search" */ '@src/views/Search/index.vue'),
            children: [
              {
                path: '',
                name: 'overview',
                props: (route) => ({ query: route.query.query || '' }),
                component: () => import(/* webpackChunkName: "search" */ '@src/views/Search/Overview/index.vue'),
                beforeEnter: usePreferredSorting
              },
              {
                path: 'people',
                name: 'people',
                component: () => import(/* webpackChunkName: "search" */ '@src/views/Search/People/index.vue')
              },
              {
                path: 'experts',
                name: 'experts',
                beforeEnter(to, from, next) {
                  if (realmSettings?.expert_search.show_expert_search) {
                    next()
                  } else {
                    next('/error/403')
                  }
                },
                component: () => import(/* webpackChunkName: "search" */ '@src/views/Search/Experts/index.vue')
              },
              {
                path: 'questions',
                name: 'questions',
                component: () => import(/* webpackChunkName: "search" */ '@src/views/Search/Questions/index.vue'),
                beforeEnter: usePreferredSorting
              }
            ]
          },
          {
            path: 'settings',
            name: 'settings',
            component: () => import(/* webpackChunkName: "settings" */ '@src/views/Settings/Overview/index.vue')
          },
          {
            path: 'settings/password',
            name: 'settings-password',
            component: () => import(/* webpackChunkName: "settings-password" */ '@src/views/Settings/Password/index.vue')
          },
          {
            path: 'settings/language',
            name: 'settings-language',
            component: () => import(/* webpackChunkName: "settings-language" */ '@src/views/Settings/SMLanguageSettings/index.vue')
          },
          {
            path: 'settings/communication',
            name: 'settings-communication',
            component: () => import(/* webpackChunkName: "settings-communication" */ '@src/views/Settings/Communication/index.vue')
          },
          {
            path: 'settings/profile',
            name: 'settings-profile',
            component: () => import(/* webpackChunkName: "settings-profile" */ '@src/views/Settings/Profile/index.vue')
          },
          {
            path: 'spaces',
            name: 'spaces',
            component: () => import(/* webpackChunkName: "spaces" */ '@src/views/KnowledgeSpaces/components/list/KnowledgeSpacesList.vue')
          },
          {
            path: 'conversation/:conversationId?',
            name: 'conversation',
            props: (route) => ({
              conversationId: route.params.conversationId
            }),
            async beforeEnter(to, from, next) {
              const { data: user } = await usersApi.cached.getCurrentUser()
              const { data: { toggles } } = await unleashApi.cached.get(user.event_tracking_id, realmSettings!.metadata.realm_id, realmSettings!.general.default_language)
              // Once the Starmind Agent is enabled for the account, StarGPT is replaced: silently redirect to the Agent.
              if (toggles.find((t) => t.name === FeatureToggleNames.RELEASE_STARDEV_11519_AGENT_ROLLOUT)?.enabled) {
                next('/agent')
                return
              }
              // Any user with a link to a specific conversation can enter. Only the ones that have the ft enabled can start one
              if (!!to.params.conversationId || toggles.find((t) => t.name === FeatureToggleNames.RELEASE_LLM_CONVERSATIONS)?.enabled) {
                next()
              } else {
                next('/error/403')
              }
            },
            component: () => import(/* webpackChunkName: "conversations" */ '@src/views/Conversations/index.vue')
          },
          {
            path: 'concept',
            name: 'concept-overview',
            component: () => import(/* webpackChunkName: "concept-overview" */ '@src/views/ConceptOverview/index.vue')
          },
          {
            path: 'demo-graph',
            name: 'demo-graph',
            component: () => import(/* webpackChunkName: "demo-graph" */ '@src/views/DemoGraph/index.vue')
          },
          {
            path: 'expert-connect',
            name: 'expert-connect',
            beforeEnter(to, from, next) {
              if (realmSettings?.expert_search.show_expert_search) {
                next()
              } else {
                next('/error/403')
              }
            },
            component: () => import(/* webpackChunkName: "expert-search" */ '@src/views/ExpertSearch/index.vue')
          },
          {
            path: '/new-insights',
            name: 'new-insights',
            async beforeEnter(to, _, next) {
              const { data: user } = await usersApi.cached.getCurrentUser()
              const { data: { toggles } } = await unleashApi.cached.get(user.event_tracking_id, realmSettings!.metadata.realm_id, realmSettings!.general.default_language)
              if (toggles.find((t) => t.name === FeatureToggleNames.RELEASE_STRATEGIC_FORESIGHT)?.enabled) {
                next()
              } else {
                next('/error/403')
              }
            },
            component: () => import(/* webpackChunkName: "new-insights" */ '@src/views/Insights/index.vue')
          },
          // TODO: This is not needed anymore as soon as all 'old' child components (activity, following, contribution, subscription)
          // support a string globalUserId to be passed into their userId prop.
          // Then the URL /user/:globalUserId should be the default for the user profile instead of the numeric Phoenix user id
          {
            path: 'user/global/:id', // This is the ad-interim URL to a profile if only the global_user_id is known
            component: { render: () => null },
            async beforeEnter(to) {
              try {
                const { data: { id: phoenixUserId } } = await usersApi.getUser(to.params.id as string)
                return { path: `/user/${phoenixUserId}` }
              } catch {
                return { path: '/error/404', replace: true }
              }
            }
          },
          {
            path: 'user/:id(\\d+)?',
            props: (route) => ({ userId: route.params.id ? Number(route.params.id) : undefined }), // TODO: props are not needed anymore when the old user profile is removed
            component: async () => {
              return import(/* webpackChunkName: "user" */ '@src/views/UserProfileV2/index.vue')
            },
            children: [
              {
                path: '',
                name: 'user/profile',
                component: async () => {
                  return import(/* webpackChunkName: "user" */ '@src/views/UserProfileV2/components/UserProfileMain.vue')
                }
              },
              {
                path: 'activity',
                name: 'user/activity',
                props: (route) => ({ userId: route.params.id ? Number(route.params.id) : undefined }), // TODO: props are not needed anymore when the old user profile is removed
                component: () => import(/* webpackChunkName: "user" */ '@src/views/User/components/activity/index.vue')
              },
              {
                path: 'following',
                name: 'user/following',
                props: (route) => ({ userId: route.params.id ? Number(route.params.id) : undefined }), // TODO: props are not needed anymore when the old user profile is removed
                component: () => import(/* webpackChunkName: "user" */ '@src/views/User/components/following/index.vue')
              },
              {
                path: 'contribution',
                name: 'user/contribution',
                props: (route) => ({ userId: route.params.id ? Number(route.params.id) : undefined }), // TODO: props are not needed anymore when the old user profile is removed
                component: () => import(/* webpackChunkName: "user" */ '@src/views/User/components/contribution/index.vue')
              },
              {
                path: 'subscription',
                name: 'user/subscription',
                component: () => import(/* webpackChunkName: "user" */ '@src/views/User/components/subscription/index.vue')
              }
            ]
          },
          {
            path: 'admin',
            name: 'admin',
            component: () => import(/* webpackChunkName: "admin" */ '@src/views/Admin/index.vue'),
            children: getAdminRouteConfig(realmSettings)
          }
        ]
      },
      {
        path: '/logout/',
        name: 'logout',
        component: () => import(
          /* webpackChunkName: "logout" */ '@src/views/Logout/index.vue'
        )
      },
      {
        path: '/invite/set-password/:token',
        name: 'set-password-token',
        beforeEnter(to, from, next) {
          if (realmSettings?.auth.password_auth_enabled) {
            next()
          } else {
            // redirect to arbitrary authenticated route so the user can login with SSO: DEV-10421
            next('/logout')
          }
        },
        props: (route) => ({
          token: route.params.token,
          onError: (() => router.push({ name: 'error-page-token' })) as any
        }),
        component: () => import(
          /* webpackChunkName: "set-password-token" */ '@src/views/SetPassword/index.vue'
        )
      },
      {
        path: '/invite/create-user/:token',
        name: 'create-user-token',
        props: (route) => ({
          token: route.params.token,
          onError: () => router.push({ name: 'error-page-token' })
        }),
        component: () => import(
          /* webpackChunkName: "create-user-from-token" */ '@src/views/CreateUserFromToken/index.vue'
        )
      },
      {
        path: '/confirm-email/:token',
        name: 'confirm-email-token',
        props: (route) => ({
          token: route.params.token,
          onComplete: () => {
            // TODO once we migrate the homepage use router.push({ name: 'homepage' })
            window.location.assign('/')
          }
        }),
        component: () => import(
          /* webpackChunkName: "confirm-email-token" */ '@src/views/ConfirmEmail/index.vue'
        )
      },
      {
        path: '/external-app/',
        name: 'external-app',
        component: () => import(
          /* webpackChunkName: "external-app" */ '@src/views/ExternalApp/index.vue'
        )
      },
      {
        path: '/invite/error/token/:token',
        name: 'error-page-token',
        component: () => import(
          /* webpackChunkName: "error-page-token" */ '@src/views/ErrorPage/InvalidToken/index.vue'
        )
      },
      {
        path: '/error/',
        children: [
          {
            path: '401',
            name: 'error401',
            props: (route) => ({ errorCode: route.query.code }),
            component: () => import(/* webpackChunkName: "error401" */ '@src/views/ErrorPage/401/index.vue')
          },
          {
            path: '503',
            name: 'error503',
            component: () => import(/* webpackChunkName: "error503" */ '@src/views/ErrorPage/503/index.vue')
          },
          {
            path: '500',
            name: 'error500',
            component: () => import(/* webpackChunkName: "error500" */ '@src/views/ErrorPage/500/index.vue')
          },
          {
            path: '403',
            name: 'error403',
            component: () => import(/* webpackChunkName: "error403" */ '@src/views/ErrorPage/403/index.vue')
          },
          {
            path: '404',
            name: 'error404',
            component: () => import(/* webpackChunkName: "error404" */ '@src/views/ErrorPage/404/index.vue')
          }
        ]
      },
      {
        path: '/notifications',
        name: 'notifications',
        redirect() {
          return { path: '/?notifications=true' }
        }
      },
      {
        path: '/ask',
        name: 'ask',
        redirect(to) {
          const query: Record<string, string> = {
            askDialog: (to.query.title as string) || '_empty_'
          }
          if (to.query.categoryId) {
            query.categoryId = to.query.categoryId as string
          }
          if (to.query.knowledgeSpaceId) {
            query.knowledgeSpaceId = to.query.knowledgeSpaceId as string
          }
          return { path: '/', query }
        }
      },
      {
        path: '/announcement/:id',
        name: 'announcement',
        redirect(to) {
          const { id } = to.params
          return { path: `/?announcementId=${id}` }
        }
      },
      {
        path: '/question/:id/edit',
        name: 'question-draft-edit',
        redirect(to) {
          if (!Number.isNaN(parseInt(to.params.id as string, 10))) {
            return { path: `/?askDialogDraft=${to.params.id}` }
          }
          return { path: '/' }
        }
      },
      {
        // Must stay last. Vue Router has no implicit 404, so without this an unknown URL
        // matches nothing and renders a blank page. Rendering the page in place instead of
        // redirecting to /error/404 keeps the bad URL in the address bar, which is what you
        // want when tracking down a broken link.
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: () => import(/* webpackChunkName: "error404" */ '@src/views/ErrorPage/404/index.vue')
      }
    ]
  })

  router.beforeEach(roleBasedNavigationGuard) // ensures that the requiredRole 'meta' prop of each route is enforced

  routerCreated = true

  return router
}
